import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CookieConsent } from '@/components/CookieConsent';
import { SupportChatWidget } from '@/components/website/SupportChatWidget';
import { UpgradeRequiredModal } from '@/components/UpgradeRequiredModal';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ReminderProvider } from '@/contexts/ReminderContext';
import { SuperAdminAuthProvider } from '@/contexts/SuperAdminAuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RequirePermission } from '@/components/RequirePermission';
import { SuperAdminRoute } from '@/components/super-admin/SuperAdminRoute';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { PrivacyPolicyPage, TermsOfServicePage } from '@/pages/website/LegalPages';
import { UnsubscribePage } from '@/pages/UnsubscribePage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ChoosePlanPage } from '@/pages/ChoosePlanPage';
import { PaymentPage } from '@/pages/PaymentPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { LeadsPage } from '@/pages/LeadsPage';
import { LeadDetailPage } from '@/pages/LeadDetailPage';
import { LeadTrashPage } from '@/pages/LeadTrashPage';
import { UsersPage } from '@/pages/UsersPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { RolesPage } from '@/pages/RolesPage';
import { AdminModulesPage } from '@/pages/admin/AdminModulesPage';
import { TasksPage } from '@/pages/TasksPage';
import { RemindersPage } from '@/pages/RemindersPage';
import { SupportPage } from '@/pages/SupportPage';
import { CommunicationLogsPage } from '@/pages/CommunicationLogsPage';
import { SupportTicketDetailPage } from '@/pages/support/SupportTicketDetailPage';
import { LeadAssignPage } from '@/pages/LeadAssignPage';
import { TeamsPage } from '@/pages/TeamsPage';
import { TimeAvailabilityPage } from '@/pages/TimeAvailabilityPage';
import { CampaignsPage } from '@/pages/CampaignsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { EmailTemplatesPage } from '@/pages/EmailTemplatesPage';
import { SmsTemplatesPage } from '@/pages/SmsTemplatesPage';
import { WhatsAppChatPage } from '@/pages/WhatsAppChatPage';
import WhatsAppInbox from '@/features/whatsapp/pages/WhatsAppInbox';
import WhatsAppTemplatesPage from '@/features/whatsapp/pages/WhatsAppTemplatesPage';
import WhatsAppBroadcastsPage from '@/features/whatsapp/pages/WhatsAppBroadcastsPage';
import WhatsAppBroadcastDetailPage from '@/features/whatsapp/pages/WhatsAppBroadcastDetailPage';
import WhatsAppSettingsPage from '@/features/whatsapp/pages/WhatsAppSettingsPage';
import WorkflowsPage from '@/features/workflow/pages/WorkflowsPage';
import { AiHubPage } from '@/pages/AiHubPage';
import WhatsAppQualityPage from '@/features/whatsapp/pages/WhatsAppQualityPage';
import WhatsAppCompliancePage from '@/features/whatsapp/pages/WhatsAppCompliancePage';
import { BillingPage } from '@/pages/BillingPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { BookingPage } from '@/pages/BookingPage';
import { BookDemoPage } from '@/pages/BookDemoPage';
import { PublicLeadFormPage } from '@/pages/PublicLeadFormPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { AuditLogPage } from '@/pages/AuditLogPage';
import { DeveloperPage } from '@/pages/DeveloperPage';
import { ApiDocsPage } from '@/pages/ApiDocsPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SuperAdminLoginPage } from '@/pages/super-admin/SuperAdminLoginPage';
import { SuperAdminDashboardPage } from '@/pages/super-admin/SuperAdminDashboardPage';
import { SuperAdminOrganizationsPage } from '@/pages/super-admin/SuperAdminOrganizationsPage';
import { SuperAdminPlansPage } from '@/pages/super-admin/SuperAdminPlansPage';
import { SuperAdminRevenuePage } from '@/pages/super-admin/SuperAdminRevenuePage';
import { SuperAdminTicketsPage } from '@/pages/super-admin/SuperAdminTicketsPage';
import { SuperAdminTicketDetailPage } from '@/pages/super-admin/SuperAdminTicketDetailPage';
import { SuperAdminProspectsPage } from '@/pages/super-admin/SuperAdminProspectsPage';
import { SuperAdminDemoSettingsPage } from '@/pages/super-admin/SuperAdminDemoSettingsPage';
import { SuperAdminFeedbackPage } from '@/pages/super-admin/SuperAdminFeedbackPage';
import { SuperAdminCampaignsPage } from '@/pages/super-admin/SuperAdminCampaignsPage';
import { SuperAdminSettingsPage } from '@/pages/super-admin/SuperAdminSettingsPage';
import { SuperAdminModulesPage } from '@/pages/super-admin/SuperAdminModulesPage';
import { SuperAdminRolesPage } from '@/pages/super-admin/SuperAdminRolesPage';
import { SuperAdminUsersPage } from '@/pages/super-admin/SuperAdminUsersPage';
import { SuperAdminEmailTemplatesPage } from '@/pages/super-admin/SuperAdminEmailTemplatesPage';
import { SuperAdminSmsTemplatesPage } from '@/pages/super-admin/SuperAdminSmsTemplatesPage';
import SuperAdminWhatsAppTemplatesPage from '@/pages/super-admin/SuperAdminWhatsAppTemplatesPage';
import SuperAdminAiPage from '@/pages/super-admin/SuperAdminAiPage';
import { ToastContainer } from '@/components/ui/toast';

// app.brixlead.com is the CRM app — land on the dashboard when signed in,
// else the login screen. The marketing site lives on www.brixlead.com.
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="h-6 w-6 rounded-full border-2 border-[#6366F1] border-t-transparent animate-spin" />
      </div>
    );
  }
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
}

// Marketing routes on the app domain bounce to the public marketing site.
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <SuperAdminAuthProvider>
        <AuthProvider>
          <ReminderProvider>
            <ToastContainer />
            <BrowserRouter>
            {/* CookieConsent uses <Link> so it must sit inside the
                BrowserRouter. Placing it outside makes useContext return
                null and crashes the whole app. */}
            <CookieConsent />
            {/* Floating "How can we help?" widget — only visible on the
                marketing site (the widget hides itself on /dashboard, /leads,
                etc. via its own routing check). */}
            <SupportChatWidget />
            {/* Listens for the brixlead:upgrade-required event the axios
                interceptor dispatches on any 402 response. Mounted globally
                so any gated API call surfaces the same upgrade prompt. */}
            <UpgradeRequiredModal />
            <Routes>
              {/* Root of the app domain → the app itself (login, or the
                  dashboard if already signed in). The marketing website lives
                  on www.brixlead.com, not here. */}
              <Route path="/" element={<RootRedirect />} />

              {/* Marketing pages belong on www.brixlead.com — bounce there if
                  someone hits them on the app domain. */}
              <Route path="/features" element={<ExternalRedirect to="https://www.brixlead.com/features" />} />
              <Route path="/pricing" element={<ExternalRedirect to="https://www.brixlead.com/pricing" />} />
              <Route path="/about" element={<ExternalRedirect to="https://www.brixlead.com/about" />} />
              <Route path="/help" element={<ExternalRedirect to="https://www.brixlead.com/help" />} />
              <Route path="/contact" element={<ExternalRedirect to="https://www.brixlead.com/contact" />} />

              {/* Legal + email-unsubscribe stay on the app domain (linked from
                  transactional emails and in-app), with the website chrome. */}
              <Route element={<WebsiteLayout />}>
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
              </Route>

              {/* Public API documentation — no auth required so vendors
                  can link to it from their wikis / Zapier app description. */}
              <Route path="/docs/api" element={<ApiDocsPage />} />

              {/* Public vendor routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Post-registration flow */}
              <Route path="/choose-plan" element={<ProtectedRoute><ChoosePlanPage /></ProtectedRoute>} />
              <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />

              {/* Vendor app */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/leads" element={<ProtectedRoute><RequirePermission module="Leads"><LeadsPage /></RequirePermission></ProtectedRoute>} />
              <Route path="/leads/trash" element={<ProtectedRoute><LeadTrashPage /></ProtectedRoute>} />
              <Route path="/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/roles" element={<ProtectedRoute><RolesPage /></ProtectedRoute>} />
              <Route path="/admin/modules" element={<ProtectedRoute><AdminModulesPage /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute><RequirePermission module="Tasks"><TasksPage /></RequirePermission></ProtectedRoute>} />
              <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
              <Route path="/support/:id" element={<ProtectedRoute><SupportTicketDetailPage /></ProtectedRoute>} />
              <Route path="/communication-logs" element={<ProtectedRoute><CommunicationLogsPage /></ProtectedRoute>} />
              <Route path="/lead-assign" element={<ProtectedRoute><LeadAssignPage /></ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute><TeamsPage /></ProtectedRoute>} />
              <Route path="/time-availability" element={<ProtectedRoute><TimeAvailabilityPage /></ProtectedRoute>} />
              <Route path="/campaigns" element={<ProtectedRoute><RequirePermission module="Campaigns"><CampaignsPage /></RequirePermission></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><RequirePermission module="Reports"><ReportsPage /></RequirePermission></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
              <Route path="/email-templates" element={<ProtectedRoute><EmailTemplatesPage /></ProtectedRoute>} />
              <Route path="/sms-templates" element={<ProtectedRoute><SmsTemplatesPage /></ProtectedRoute>} />
              {/* WhatsApp:
                  /whatsapp           → redirect to the new multi-tenant inbox
                  /whatsapp/inbox     → Phase 8 inbox (new)
                  /whatsapp/legacy    → old paste-token UI, kept until Phase 8b cutover */}
              <Route path="/whatsapp" element={<Navigate to="/whatsapp/inbox" replace />} />
              <Route path="/whatsapp/inbox" element={<ProtectedRoute><WhatsAppInbox /></ProtectedRoute>} />
              <Route path="/whatsapp/templates" element={<ProtectedRoute><WhatsAppTemplatesPage /></ProtectedRoute>} />
              <Route path="/whatsapp/broadcasts" element={<ProtectedRoute><WhatsAppBroadcastsPage /></ProtectedRoute>} />
              <Route path="/whatsapp/broadcasts/:id" element={<ProtectedRoute><WhatsAppBroadcastDetailPage /></ProtectedRoute>} />
              <Route path="/whatsapp/settings" element={<ProtectedRoute><WhatsAppSettingsPage /></ProtectedRoute>} />
              <Route path="/workflows" element={<ProtectedRoute><RequirePermission module="Workflows"><WorkflowsPage /></RequirePermission></ProtectedRoute>} />
              <Route path="/ai" element={<ProtectedRoute><AiHubPage /></ProtectedRoute>} />
              <Route path="/voice-agents" element={<Navigate to="/ai" replace />} />
              <Route path="/automations" element={<Navigate to="/workflows" replace />} />
              <Route path="/whatsapp/quality" element={<ProtectedRoute><WhatsAppQualityPage /></ProtectedRoute>} />
              <Route path="/whatsapp/compliance" element={<ProtectedRoute><WhatsAppCompliancePage /></ProtectedRoute>} />
              <Route path="/whatsapp/legacy" element={<ProtectedRoute><WhatsAppChatPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
              <Route path="/developer" element={<ProtectedRoute><DeveloperPage /></ProtectedRoute>} />
              <Route path="/book/:userId/:eventSlug" element={<BookingPage />} />
              {/* Short per-lead booking link */}
              <Route path="/m/:code" element={<BookingPage />} />
              {/* Public "Book a Demo" — super-admin demo booking, NO auth.
                  /book-demo (no id) round-robins to an available super-admin. */}
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/book-demo/:adminId" element={<BookDemoPage />} />

              {/* Public lead-form intake — NO auth. Used for embedding on
                  tenant marketing sites + as a QA tool for auto-assign rules.
                  Both shapes work:
                    /lead-form/<tenantId>
                    /lead-form?tenant=<tenantId>  */}
              <Route path="/lead-form/:tenantId" element={<PublicLeadFormPage />} />
              <Route path="/lead-form" element={<PublicLeadFormPage />} />

              {/* Super Admin routes */}
              <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
              <Route path="/super-admin/dashboard" element={<SuperAdminRoute><SuperAdminDashboardPage /></SuperAdminRoute>} />
              <Route path="/super-admin/organizations" element={<SuperAdminRoute><SuperAdminOrganizationsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/plans" element={<SuperAdminRoute><SuperAdminPlansPage /></SuperAdminRoute>} />
              <Route path="/super-admin/revenue" element={<SuperAdminRoute><SuperAdminRevenuePage /></SuperAdminRoute>} />
              <Route path="/super-admin/tickets" element={<SuperAdminRoute><SuperAdminTicketsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/tickets/:id" element={<SuperAdminRoute><SuperAdminTicketDetailPage /></SuperAdminRoute>} />
              <Route path="/super-admin/prospects" element={<SuperAdminRoute><SuperAdminProspectsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/ai-assistant" element={<SuperAdminRoute><SuperAdminAiPage /></SuperAdminRoute>} />
              <Route path="/super-admin/demos" element={<SuperAdminRoute><SuperAdminDemoSettingsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/feedback" element={<SuperAdminRoute><SuperAdminFeedbackPage /></SuperAdminRoute>} />
              <Route path="/super-admin/campaigns" element={<SuperAdminRoute><SuperAdminCampaignsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/email-templates" element={<SuperAdminRoute><SuperAdminEmailTemplatesPage /></SuperAdminRoute>} />
              <Route path="/super-admin/sms-templates" element={<SuperAdminRoute><SuperAdminSmsTemplatesPage /></SuperAdminRoute>} />
              <Route path="/super-admin/whatsapp-templates" element={<SuperAdminRoute><SuperAdminWhatsAppTemplatesPage /></SuperAdminRoute>} />
              {/* Lead Sources & Lead Statuses now live as tabs inside /super-admin/settings —
                  keep redirects so any existing bookmarks still land somewhere sensible. */}
              <Route path="/super-admin/lead-sources" element={<Navigate to="/super-admin/settings" replace />} />
              <Route path="/super-admin/lead-statuses" element={<Navigate to="/super-admin/settings" replace />} />
              <Route path="/super-admin/modules" element={<SuperAdminRoute><SuperAdminModulesPage /></SuperAdminRoute>} />
              <Route path="/super-admin/roles" element={<SuperAdminRoute><SuperAdminRolesPage /></SuperAdminRoute>} />
              <Route path="/super-admin/users" element={<SuperAdminRoute><SuperAdminUsersPage /></SuperAdminRoute>} />
              <Route path="/super-admin/settings" element={<SuperAdminRoute><SuperAdminSettingsPage /></SuperAdminRoute>} />
              <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </ReminderProvider>
      </AuthProvider>
    </SuperAdminAuthProvider>
    </ErrorBoundary>
  );
}
